<template>
  <DashboardCard :title="$t('Sourcing activity')">
    <WdsTabs secondary fitted>
      <WdsTab :title="$t('Unfilled jobs')" tab-key="unfilled-jobs" class="tw-w-full">
        <template #title>
          <WdsStack align-center gap-sm>
            {{ $t('Unfilled jobs') }}
            <WdsLabel v-if="unfilledCount > 0" xs><span class="tw-px-[2px]">{{ unfilledCount }}</span></WdsLabel>
          </WdsStack>
        </template>
        <WdsStack direction-column>
          <WdsStack justify-space-between align-center class="tw-pb-2">
            <WdsMenu placement="bottom-end" z-index="50" manual-trigger>
              <template #trigger="{ toggle }">
                <WdsButtonGhost @click="toggle">
                  <WdsStack justify-center align-center gap-sm>
                    <span>{{ $t(selectedSort.label) }}</span>
                    <span>
                      <WdsIcon :size="16" name="chevron-down"></WdsIcon>
                    </span>
                  </WdsStack>
                </WdsButtonGhost>
              </template>
              <WdsMenuOption
                v-for="option in sortOptions"
                :key="option.label"
                :active="selectedSort.label === option.label"
                @click="updateSort(option)"
              >
                {{ option.label }}
                <WdsIcon
                  v-if="selectedSort.label === option.label"
                  name="checkmark"
                  :size="16"
                  color="#5d36f1"
                />
              </WdsMenuOption>
            </WdsMenu>
            <WdsButtonGhost
              :href="laravelNamedRoute('frontend.jobs.index.created')"
              secondary
              class="tw-items-center"
            >
              {{ $t('See all') }}
              <WdsIcon :size="16" name="chevron-right" />
            </WdsButtonGhost>
          </WdsStack>

          <div v-if="loading" class="loading-list">
            <LoadingList :count="3" />
          </div>

          <WdsStack v-else-if="unfilledCount === 0" align-center justify-center class="tw-pt-5 tw-pb-8">
            <WdsText secondary>{{ $t('You have no unfilled jobs') }}</WdsText>
          </WdsStack>

          <div v-else class="job-list">
            <WdsStack
              v-for="job in unfilledJobs"
              :key="job.id"
              justify-space-between
              class="job-container"
            >
              <a
                class="job tw-w-full tw-h-full"
                :href="laravelNamedRoute('jobs.show', { job: job.id })"
              >
                <WdsStack align-center gap-sm>
                  <WdsStack direction-column>
                    <div>
                      <WdsStack class="tw-pb-1.5 tw-items-center" gap-sm>
                        <WdsTooltip
                            right
                            global
                            :text="
                              isShared(job)
                                ? $t('This job is shared with at least one: TC, SA, Marketplace')
                                : $t('This job has not been shared yet')
                            "
                          >
                          <WdsTag :primary="isShared(job)">
                            <WdsStack>
                              <WdsIcon name="share" :size="14" />
                            </WdsStack>
                          </WdsTag>
                        </WdsTooltip>
                        <WdsTooltip
                            right
                            global
                            :text="$t('Number of eligible candidates in Candidate Tracker')"
                          >
                          <WdsTag sm success>
                            <WdsStack align-center justify-center>
                              <WdsIcon name="user" :size="14" />
                              <span class="tw-pl-0.5">{{ candidateCount(job) }}</span>
                            </WdsStack>
                          </WdsTag>
                        </WdsTooltip>
                        <WdsTooltip
                            right
                            global
                            :text="$t('Number of hires / number of required workers')"
                          >
                          <WdsTag v-if="(job.requiredWorkers ?? 1) > 1" sm>
                            <WdsStack align-center justify-center>
                              <WdsIcon name="report" :size="14" />
                              <span class="tw-pl-0.5">
                                {{ activeHires(job) }} / {{ job.requiredWorkers }}
                              </span>
                            </WdsStack>
                          </WdsTag>
                        </WdsTooltip>
                      </WdsStack>
                      <WdsText class="tw-pb-0.5">
                        {{ job.name }}
                      </WdsText>
                      <WdsText small secondary>
                        {{ startDate(job) }}
                        -
                        {{ endDate(job) }}
                      </WdsText>
                    </div>
                  </WdsStack>
                </WdsStack>
              </a>
              <WdsStack align-center justify-center class="tw-ps-4">
                <WdsMenu placement="bottom-end" z-index="50" manual-trigger>
                  <template #trigger="{ toggle }">
                    <WdsButtonGhost @click="toggle">
                      <WdsIcon name="overflow-menu-horizontal" :size="16" />
                    </WdsButtonGhost>
                  </template>
                  <div>
                    <WdsMenuOption @click="goToEditJob(job)">
                      {{ $t('Overview') }}
                    </WdsMenuOption>
                    <WdsMenuOption @click="goToEditJob(job, 'details')">
                      {{ $t('Details') }}
                    </WdsMenuOption>
                    <WdsMenuOption @click="goToEditJob(job, 'budget')">
                      {{ $t('Budget') }}
                    </WdsMenuOption>
                    <WdsMenuOption
                      :href="laravelNamedRoute(laravelNamedRoutes.jobs.candidates, { job: job.id })"
                    >
                      {{ $t('Sharing') }}
                    </WdsMenuOption>
                    <WdsMenuDivider />
                    <WdsMenuOption theme="DESTRUCTIVE" @click="endJob(job)">
                      <WdsIcon name="close-outline" :size="16" />
                      {{ $t('End job') }}
                    </WdsMenuOption>
                  </div>
                </WdsMenu>
              </WdsStack>
            </WdsStack>
          </div>
        </WdsStack>
      </WdsTab>
      <WdsTab :title="$t('Your draft jobs')" tab-key="draft-jobs">
        <template #title>
          <WdsStack align-center gap-sm>
            {{ $t('Your draft jobs') }}
            <WdsLabel v-if="draftCount > 0" xs><span class="tw-px-[2px]">{{ draftCount }}</span></WdsLabel>
          </WdsStack>
        </template>
        <WdsStack direction-column>
          <WdsStack justify-space-between align-center class="tw-pb-2">
            <WdsMenu placement="bottom-end" z-index="50" manual-trigger>
              <template #trigger="{ toggle }">
                <WdsButtonGhost @click="toggle">
                  <WdsStack justify-center align-center gap-sm>
                    <span>{{ $t(selectedSort.label) }}</span>
                    <span><WdsIcon :size="16" name="chevron-down"></WdsIcon></span>
                  </WdsStack>
                </WdsButtonGhost>
              </template>
              <WdsMenuOption
                v-for="option in sortOptions"
                :key="option.label"
                :active="selectedSort.label === option.label"
                @click="updateSort(option)"
              >
                {{ option.label }}
                <WdsIcon
                  v-if="selectedSort.label === option.label"
                  name="checkmark"
                  :size="16"
                  color="#5d36f1"
                />
              </WdsMenuOption>
            </WdsMenu>
            <WdsButtonGhost
              :href="laravelNamedRoute('frontend.jobs.index.created')"
              secondary
              class="tw-items-center"
            >
              {{ $t('See all') }}
              <WdsIcon :size="16" name="chevron-right" />
            </WdsButtonGhost>
          </WdsStack>

          <div v-if="loading">
            <LoadingList :count="3" avatar />
          </div>

          <WdsStack v-else-if="draftCount === 0" align-center justify-center class="tw-pt-5 tw-pb-8">
            <WdsText secondary>{{ $t('You have no draft jobs') }}</WdsText>
          </WdsStack>

          <div v-else class="job-list">
            <WdsStack
              v-for="job in draftJobs"
              :key="job.id"
              justify-space-between
              class="job-container"
            >
              <a
                class="job tw-w-full tw-h-full"
                :href="laravelNamedRoute('yjob.edit', { yjob: job.id })"
              >
                <WdsText small secondary class="tw-pb-0.5">
                  {{ $t('Last edited') }}: {{ new Date(job.updatedAt).toDateString() }}
                </WdsText>
                <WdsText>{{ job.name }}</WdsText>
              </a>
              <WdsStack align-center justify-center class="tw-ps-4">
                <WdsMenu placement="bottom-end" z-index="50" manual-trigger>
                  <template #trigger="{ toggle }">
                    <WdsButtonGhost @click="toggle">
                      <WdsIcon name="overflow-menu-horizontal" :size="16" />
                    </WdsButtonGhost>
                  </template>
                  <div>
                    <WdsMenuOption @click="goToEditJob(job)">
                      {{ $t('Edit draft') }}
                    </WdsMenuOption>
                    <WdsMenuDivider />
                    <WdsMenuOption theme="DESTRUCTIVE" @click="endJob(job)">
                      <WdsIcon name="trash-can" :size="16" />
                      {{ $t('Remove draft') }}
                    </WdsMenuOption>
                  </div>
                </WdsMenu>
              </WdsStack>
            </WdsStack>
          </div>
        </WdsStack>
      </WdsTab>
    </WdsTabs>
  </DashboardCard>
</template>

<script setup lang="ts">
import {
  WdsLabel,
  WdsStack,
  WdsTab,
  WdsTabs,
  WdsText,
  WdsMenu,
  WdsButtonGhost,
  WdsIcon,
  WdsMenuOption,
  WdsMenuDivider,
  WdsTag,
  WdsTooltip,
} from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import swal from 'sweetalert'
import DashboardCard from '@/components/Dashboard/DashboardCard.vue'
import { $t } from '@/composables/translation'
import { graphql } from '@/gql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import laravelNamedRoutes from '@/utils/laravelNamedRoutes.json'
import { EndJobMutation } from '@/components/Job/graphql/mutations'
import { JobEndTimeframe, JobStartTimeframe, JobOrderByColumn, SortOrder } from '@/gql/graphql'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'

const sortOptions = [
  { label: $t('Latest'), orderBy: JobOrderByColumn.CreatedAt, order: SortOrder.Desc },
  { label: $t('Oldest'), orderBy: JobOrderByColumn.CreatedAt, order: SortOrder.Asc },
]

const selectedSort = ref(sortOptions[0])

const { result, loading, refetch } = useQuery(
  graphql(`
    query JobsList($accounts: [ID!], $orderBy: JobOrderByColumn!, $order: SortOrder!) {
      availableJobs: jobs(
        accounts: $accounts
        unfilled: true
        published: true
        first: 10000
        orderBy: { field: $orderBy, order: $order }
      ) {
        data {
          id
          number
          name
          startDateTimeframe
          endDateTimeframe
          startDate
          endDate
          requiredWorkers
          isForMarketplace
          updatedAt
          hires {
            data {
              id
              activeContract {
                id
                workerSignature
              }
            }
          }
          jobCandidates {
            data {
              id
            }
          }
          shares {
            data {
              id
            }
          }
        }
      }
      draftJobs: jobs(
        accounts: $accounts
        unfilled: true
        published: false
        first: 10000
        orderBy: { field: $orderBy, order: $order }
      ) {
        data {
          id
          number
          name
          updatedAt
        }
      }
    }
  `),
  {
    accounts: [currentAccount.globalId],
    orderBy: selectedSort.value.orderBy,
    order: selectedSort.value.order,
  },
  {
    clientId: 'publicApi',
  },
)

const updateSort = (option) => {
  selectedSort.value = option
  refetch({
    accounts: [currentAccount.globalId],
    orderBy: option.orderBy,
    order: option.order,
  })
}

const { mutate: endJobMutation, loading: endJobLoading } = useMutation(EndJobMutation, {
  clientId: 'publicApi',
})

const activeHires = (job) =>
  job.hires.data.filter((hire) => hire.activeContract?.workerSignature !== undefined).length

const candidateCount = (job) => job.jobCandidates.data.length

const isShared = (job) => job.shares.data.length > 0 || job.isForMarketplace

const startDate = (job) => {
  if (job.startDate) {
    return job.startDate
  }

  if (job.startDateTimeframe) {
    switch (job.startDateTimeframe) {
      case JobStartTimeframe.OnDate:
        return $t('On date')
      case JobStartTimeframe.Asap:
        return $t('Asap')
      case JobStartTimeframe.NextMonth:
        return $t('Next month')
    }
  }

  return '?'
}

const endDate = (job) => {
  if (job.endDate) {
    return job.endDate
  }

  if (job.endDateTimeframe) {
    switch (job.endDateTimeframe) {
      case JobEndTimeframe.OnDate:
        return $t('On date')
      case JobEndTimeframe.OneMonth:
        return $t('One month')
      case JobEndTimeframe.Open:
        return $t('Open')
      case JobEndTimeframe.SixMonths:
        return $t('Six months')
      case JobEndTimeframe.ThreeMonths:
        return $t('Three months')
    }
  }

  return '?'
}

const unfilledJobs = computed(() => result?.value?.availableJobs.data ?? [])
const draftJobs = computed(() => result?.value?.draftJobs.data ?? [])

const unfilledCount = computed(() => unfilledJobs.value.length)
const draftCount = computed(() => draftJobs.value.length)

const goToEditJob = (job, tab: string | undefined = undefined) => {
  window.location.href =
    laravelNamedRoute(laravelNamedRoutes.yjob.edit, { yjob: job.id }) + (tab ? `?tab=${tab}` : '')
}

const endJob = async (job) => {
  swal(
    {
      title: 'End job?',
      text: 'Do you want to terminate this job and terminate any ongoing job process?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, mark the job as completed',
      cancelButtonText: 'No, go back!',
      closeOnConfirm: false,
    },
    async () => {
      swal($t('Processing'), $t('Marking the job as completed'), 'info')

      await endJobMutation({
        input: {
          id: job.id,
          accountId: currentAccount.globalId,
        },
      })

      refetch()

      swal({
        title: $t('Success'),
        text: $t('The job was successfully closed'),
        type: 'success',
        timer: 2000,
      })
    },
  )
}
</script>

<style lang="scss" scoped>
.job-container {
  @apply tw-bg-white;
  @apply tw-rounded;
  @apply tw-py-1;
  @apply tw-pl-1;
  @apply tw-pr-5;
  @apply tw-mb-2;
  @apply tw-border-2;
  @apply tw-border-transparent;
  @apply tw-transition;
  @apply tw-duration-200;
}
.job-list {
  @apply tw-min-h-10;
  @apply tw-max-h-[360px];
  @apply tw-overflow-y-auto;
  @apply tw-relative;
  @apply tw-pb-2;
}
.job-container:hover {
  @apply tw-border-2;
  @apply tw-border-gray-200;
}
.job {
  @apply tw-text-left;
  @apply tw-p-1;
  @apply tw-px-2;
}
.loading-container {
  @apply tw-w-full;
  @apply tw-bg-white;
  @apply tw-rounded;
  @apply tw-py-1;
  @apply tw-p-3;
  @apply tw-px-4;
  @apply tw-mt-2;
}
.loading {
  @apply tw-w-3/5;
  @apply tw-h-[20px];
  @apply tw-bg-gray-200;
  @apply tw-rounded-xl;
  @apply tw-animate-pulse;
}
.loading-secondary {
  @apply tw-w-2/5;
  @apply tw-h-[20px];
  @apply tw-bg-gray-100;
  @apply tw-rounded-xl;
  @apply tw-animate-pulse;
}
.loading-list {
  @apply tw-pb-4;
}
</style>
